export const PATROL_LAB_STAGES = [
  'briefing',
  'wall',
  'observe',
  'appear',
  'engage',
  'help',
  'preflight',
] as const;
export type PatrolLabStage = (typeof PATROL_LAB_STAGES)[number];

export const PATROL_LAB_ACTORS = ['supervisor', 'studentA', 'studentB'] as const;
export type PatrolLabActor = (typeof PATROL_LAB_ACTORS)[number];

export const PATROL_LAB_PREFLIGHT_STATES = [
  'waiting',
  'ready',
  'mixed',
  'retakeDialog',
  'retakeRequested',
  'cleared',
  'late',
] as const;
export type PatrolLabPreflightState = (typeof PATROL_LAB_PREFLIGHT_STATES)[number];

export const PATROL_LAB_DEFAULT_PREFLIGHT_STATE: PatrolLabPreflightState = 'ready';

export const PATROL_LAB_MAX_STUDENTS = 6;

export const PATROL_LAB_HAND_TARGETS = ['hermione', 'harry'] as const;
export type PatrolLabHandTarget = (typeof PATROL_LAB_HAND_TARGETS)[number];

export const PATROL_LAB_QUESTION_VARIANTS = ['short', 'sitting', 'longest'] as const;
export type PatrolLabQuestionVariant = (typeof PATROL_LAB_QUESTION_VARIANTS)[number];

export const PATROL_LAB_CLOCK_MODES = ['reading', 'answering', 'paused'] as const;
export type PatrolLabClockMode = (typeof PATROL_LAB_CLOCK_MODES)[number];

export type PatrolLabQuery = {
  stage: PatrolLabStage;
  actor: PatrolLabActor;
  /** Students on the wall, 1–6. Observe and Appear need Hermione, so they keep at least 2. */
  students?: number;
  /** Raised-hand student on the observe strip (`hand=hermione`). */
  hand?: PatrolLabHandTarget;
  /** Completed students on the overview wall (`submitted=2`). */
  submitted?: number;
  /** Question length preset for student and observe payloads. */
  question?: PatrolLabQuestionVariant;
  /** Timer preset for commissioning reading, answering, or paused clocks. */
  clock?: PatrolLabClockMode;
  /** Device clock skew in seconds for server-time commissioning (`skew=12`). */
  skew?: number;
  /** Pre-flight review commissioning state when stage is `preflight`. */
  state?: PatrolLabPreflightState;
};

function parseStudentCount(raw: string | null): number | undefined {
  const count = Number.parseInt(raw ?? '', 10);
  if (!Number.isFinite(count) || count >= PATROL_LAB_MAX_STUDENTS) return undefined;
  return Math.max(1, count);
}

function parseSubmittedCount(raw: string | null): number | undefined {
  const count = Number.parseInt(raw ?? '', 10);
  if (!Number.isFinite(count) || count <= 0) return undefined;
  return Math.min(count, PATROL_LAB_MAX_STUDENTS);
}

function parseHandTarget(raw: string | null): PatrolLabHandTarget | undefined {
  if (!raw) return undefined;
  return PATROL_LAB_HAND_TARGETS.includes(raw as PatrolLabHandTarget)
    ? (raw as PatrolLabHandTarget)
    : undefined;
}

function parseQuestionVariant(raw: string | null): PatrolLabQuestionVariant | undefined {
  if (!raw) return undefined;
  return PATROL_LAB_QUESTION_VARIANTS.includes(raw as PatrolLabQuestionVariant)
    ? (raw as PatrolLabQuestionVariant)
    : undefined;
}

function parseClockMode(raw: string | null): PatrolLabClockMode | undefined {
  if (!raw) return undefined;
  return PATROL_LAB_CLOCK_MODES.includes(raw as PatrolLabClockMode)
    ? (raw as PatrolLabClockMode)
    : undefined;
}

function parseSkewSeconds(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const seconds = Number.parseInt(raw, 10);
  if (!Number.isFinite(seconds)) return undefined;
  return seconds;
}

function parsePreflightState(raw: string | null): PatrolLabPreflightState {
  if (PATROL_LAB_PREFLIGHT_STATES.includes(raw as PatrolLabPreflightState)) {
    return raw as PatrolLabPreflightState;
  }
  return PATROL_LAB_DEFAULT_PREFLIGHT_STATE;
}

export function parsePatrolLabQuery(search: string): PatrolLabQuery {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const stage = params.get('stage');
  const actor = params.get('actor');
  const parsedStage = PATROL_LAB_STAGES.includes(stage as PatrolLabStage)
    ? (stage as PatrolLabStage)
    : 'observe';
  const query: PatrolLabQuery = {
    stage: parsedStage,
    actor: PATROL_LAB_ACTORS.includes(actor as PatrolLabActor)
      ? (actor as PatrolLabActor)
      : 'supervisor',
    students: parseStudentCount(params.get('students')),
    hand: parseHandTarget(params.get('hand')),
    submitted: parseSubmittedCount(params.get('submitted')),
    question: parseQuestionVariant(params.get('question')),
    clock: parseClockMode(params.get('clock')),
    skew: parseSkewSeconds(params.get('skew')),
  };
  if (parsedStage === 'preflight') {
    query.state = parsePreflightState(params.get('state'));
  }
  return query;
}

export function serializePatrolLabQuery(query: PatrolLabQuery): string {
  const params = new URLSearchParams();
  params.set('stage', query.stage);
  params.set('actor', query.actor);
  if (query.students !== undefined && query.students !== PATROL_LAB_MAX_STUDENTS) {
    params.set('students', String(query.students));
  }
  if (query.hand) {
    params.set('hand', query.hand);
  }
  if (query.submitted !== undefined) {
    params.set('submitted', String(query.submitted));
  }
  if (query.question) {
    params.set('question', query.question);
  }
  if (query.clock) {
    params.set('clock', query.clock);
  }
  if (query.skew !== undefined) {
    params.set('skew', String(query.skew));
  }
  if (query.stage === 'preflight' && query.state && query.state !== PATROL_LAB_DEFAULT_PREFLIGHT_STATE) {
    params.set('state', query.state);
  }
  return `?${params.toString()}`;
}
